import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';

import { i18n } from '@/shared/i18n/config';
import { ApiError } from '@/shared/api/http/client';

import { AuthProvider } from '@/shared/auth/AuthContext';
import { loadSession } from '@/shared/auth/session';

/** The service's word for "signed in, but scoped to no tenant". */
const NO_TENANT = 'tenantNotSelected';

/* Recover from a session that lost its tenant while the cockpit was reading.
 *
 * The cookie is the only record of which tenant is chosen, and it can stop
 * saying so without this tab noticing: it expires, the operator switches tenant
 * in another tab, or an administrator withdraws the grant that made the choice
 * legal. The next read then answers 403 `tenantNotSelected` — and every read
 * after it, because nothing about a failed query changes what the cookie says.
 *
 * So the fix is not to redirect from here. It is to re-ask `/auth/me`, which is
 * the one call that reports the cookie's actual state; `TenantGuard` is already
 * watching that answer and will send the operator to the picker the moment it
 * flips. One source of truth, and no navigation performed from outside the
 * router.
 *
 * Coalesced, because a screen with six queries on it produces six of these at
 * once and they would otherwise be six identical probes. */
let reprobing = false;

function recoverFromLostTenant(error: unknown): void {
  if (!(error instanceof ApiError) || error.code !== NO_TENANT) return;
  if (reprobing) return;

  reprobing = true;
  void loadSession().finally(() => {
    reprobing = false;
  });
}

/* The cockpit reads far more than it writes, and an operator moving between the
   queue and a case expects the counts to stay still. Data is never auto-polled;
   the operator explicitly reloads via the Reload button on each page header.
   A long staleTime ensures navigating away and back does not trigger a new fetch.
   The transport seam decides where the data actually comes from. */
function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: recoverFromLostTenant }),
    mutationCache: new MutationCache({ onError: recoverFromLostTenant }),
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: 5 * 60_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Drop the cache when the language changes.
 *
 * Invalidating in one place beats threading the language through every query
 * key, and covers service responses that vary prose by `Accept-Language`.
 *
 * `invalidateQueries` rather than `clear`: the mounted screens refetch, and
 * everything else is marked stale for whenever it is next read. `clear` would
 * blank the screens first and fill them second.
 */
function useRefetchOnLanguageChange(client: QueryClient): void {
  useEffect(() => {
    const onChange = (): void => {
      void client.invalidateQueries();
    };
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [client]);
}

export function Providers({ children }: { readonly children: ReactNode }): ReactElement {
  // Created once per mount, never on re-render: a fresh client would drop the cache.
  const [client] = useState(createQueryClient);
  useRefetchOnLanguageChange(client);

  /* `AuthProvider` outermost of the three because it fires the session probe on
     mount and everything below it may need to know the answer. No provider from
     an identity library above it any more — the sign-in is the service's, and
     the browser holds nothing but a cookie it cannot read. */
  return (
    <AuthProvider>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={client}>{children}</QueryClientProvider>
      </I18nextProvider>
    </AuthProvider>
  );
}
